import i18next from 'i18next';
import type { LocationTypeData, RigData, SpeciesData, TechniqueData } from '../../types';

type AccessType = 'shore' | 'boat' | 'both';

const techniqueMatchesAccess = (technique: TechniqueData, accessType: AccessType) => {
  if (accessType === 'both') return true;
  const haystack = `${technique.techniqueId} ${technique.name} ${technique.category}`.toLowerCase();
  if (accessType === 'shore') {
    return /(shore|bottom|float|bolognese|surf|rock|squid|egi|spinning|sabiki|harbor)/.test(haystack);
  }
  return /(boat|offshore|deep|drift|troll|jig|downrigger|lampuga|live bait|bottom)/.test(haystack);
};

const rigMatchesAccess = (rig: RigData, accessType: AccessType) => {
  if (accessType === 'both') return true;
  if (accessType === 'shore') {
    return ['bottom', 'float', 'surf', 'spinning', 'specialized'].includes(rig.category);
  }
  return ['trolling', 'live_bait', 'specialized', 'bottom'].includes(rig.category);
};

const locationMatchesAccess = (location: LocationTypeData, accessType: AccessType) => {
  if (accessType === 'both') return true;
  if (accessType === 'shore') {
    return ['coastal', 'harbor', 'estuary', 'structure'].includes(location.category);
  }
  return ['offshore', 'harbor', 'coastal', 'structure'].includes(location.category);
};

const beginnerLocationPenalty = (location: LocationTypeData) => {
  const haystack = `${location.locationTypeId} ${location.name} ${location.description || ''}`.toLowerCase();
  return /(protected|buffer zone|reserve|park)/.test(haystack) ? -4 : 0;
};

const beginnerLocationCategoryScore = (location: LocationTypeData) => {
  switch (location.category) {
    case 'harbor': return 4;
    case 'coastal': return 3;
    case 'estuary': return 3;
    case 'structure': return 2;
    case 'offshore': return -2;
    default: return 0;
  }
};

const beginnerLocationAccessibilityScore = (location: LocationTypeData) => {
  switch (location.accessibility) {
    case 'easy': return 3;
    case 'moderate': return 1;
    case 'difficult': return -2;
    default: return 0;
  }
};

const beginnerLocationScore = (location: LocationTypeData) => (
  beginnerLocationCategoryScore(location) + beginnerLocationAccessibilityScore(location) + beginnerLocationPenalty(location)
);

export const findPrimaryTechniqueForSpecies = (
  species: SpeciesData | null | undefined,
  techniques: TechniqueData[],
  accessType: AccessType = 'both'
): TechniqueData | null => {
  if (!species?.primaryBeginnerTechniqueId) return null;
  const technique = techniques.find((item) => item.techniqueId === species.primaryBeginnerTechniqueId) || null;
  if (!technique) return null;
  if (!species.fishingTechniqueIds.includes(technique.techniqueId) && !technique.targetSpeciesIds?.includes(species.speciesId)) {
    return null;
  }
  return techniqueMatchesAccess(technique, accessType) ? technique : null;
};

export const findPrimaryRigForTechnique = (
  technique: TechniqueData | null | undefined,
  rigs: RigData[],
  accessType: AccessType = 'both'
): RigData | null => {
  if (!technique?.primaryBeginnerRigId) return null;
  const rig = rigs.find((item) => item.rigId === technique.primaryBeginnerRigId) || null;
  if (!rig) return null;
  if (!rig.suitableTechniqueIds.includes(technique.techniqueId)) {
    return null;
  }
  return rigMatchesAccess(rig, accessType) ? rig : null;
};

export const findPrimaryLocationForSpecies = (
  species: SpeciesData | null | undefined,
  locations: LocationTypeData[],
  techniqueId?: string | null,
  accessType: AccessType = 'both'
): LocationTypeData | null => {
  if (!species?.primaryBeginnerLocationTypeId) return null;
  const location = locations.find((item) => item.locationTypeId === species.primaryBeginnerLocationTypeId) || null;
  if (!location) return null;
  if (!location.commonSpeciesIds.includes(species.speciesId)) {
    return null;
  }
  if (techniqueId && !location.recommendedTechniqueIds.includes(techniqueId) && location.primaryBeginnerTechniqueId !== techniqueId) {
    return null;
  }
  return locationMatchesAccess(location, accessType) ? location : null;
};

export interface BeginnerAnchorFlags {
  technique: boolean;
  rig: boolean;
  location: boolean;
  locationTechnique: boolean;
}

export const getBeginnerAnchorFlags = (
  species: SpeciesData | null | undefined,
  technique: TechniqueData | null | undefined,
  rig: RigData | null | undefined,
  location: LocationTypeData | null | undefined
): BeginnerAnchorFlags => ({
  technique: Boolean(species?.primaryBeginnerTechniqueId && technique && species.primaryBeginnerTechniqueId === technique.techniqueId),
  rig: Boolean(technique?.primaryBeginnerRigId && rig && technique.primaryBeginnerRigId === rig.rigId),
  location: Boolean(species?.primaryBeginnerLocationTypeId && location && species.primaryBeginnerLocationTypeId === location.locationTypeId),
  locationTechnique: Boolean(location?.primaryBeginnerTechniqueId && technique && location.primaryBeginnerTechniqueId === technique.techniqueId)
});

export const countBeginnerAnchorMatches = (flags: BeginnerAnchorFlags) =>
  Object.values(flags).filter(Boolean).length;

export const getBeginnerAnchorLabel = (flags: BeginnerAnchorFlags): string | null => {
  const count = countBeginnerAnchorMatches(flags);
  if (count >= 3) return String(i18next.t('common:homePlanner.beginnerAnchor.reviewed'));
  if (count === 2) return String(i18next.t('common:homePlanner.beginnerAnchor.good'));
  if (count === 1) return String(i18next.t('common:homePlanner.beginnerAnchor.light'));
  return null;
};

export const sortTechniquesForSpecies = (species: SpeciesData | null | undefined, techniques: TechniqueData[]) => {
  return [...techniques].sort((left, right) => {
    const leftPrimary = species?.primaryBeginnerTechniqueId === left.techniqueId ? 1 : 0;
    const rightPrimary = species?.primaryBeginnerTechniqueId === right.techniqueId ? 1 : 0;
    if (leftPrimary !== rightPrimary) return rightPrimary - leftPrimary;
    if (left.difficulty !== right.difficulty) {
      const rank = { beginner: 0, intermediate: 1, advanced: 2 } as const;
      return rank[left.difficulty] - rank[right.difficulty];
    }
    return left.name.localeCompare(right.name);
  });
};

export const sortLocationsForSpecies = (species: SpeciesData | null | undefined, techniqueId: string | null | undefined, locations: LocationTypeData[]) => {
  return [...locations].sort((left, right) => {
    const leftPrimary = species?.primaryBeginnerLocationTypeId === left.locationTypeId ? 1 : 0;
    const rightPrimary = species?.primaryBeginnerLocationTypeId === right.locationTypeId ? 1 : 0;
    if (leftPrimary !== rightPrimary) return rightPrimary - leftPrimary;
    const leftTechnique = techniqueId && left.primaryBeginnerTechniqueId === techniqueId ? 1 : 0;
    const rightTechnique = techniqueId && right.primaryBeginnerTechniqueId === techniqueId ? 1 : 0;
    if (leftTechnique !== rightTechnique) return rightTechnique - leftTechnique;
    const scoreDiff = beginnerLocationScore(right) - beginnerLocationScore(left);
    if (scoreDiff !== 0) return scoreDiff;
    return left.name.localeCompare(right.name);
  });
};

export const sortLocationsForTechnique = (technique: TechniqueData | null | undefined, locations: LocationTypeData[]) => {
  return [...locations].sort((left, right) => {
    const leftPrimary = technique && left.primaryBeginnerTechniqueId === technique.techniqueId ? 1 : 0;
    const rightPrimary = technique && right.primaryBeginnerTechniqueId === technique.techniqueId ? 1 : 0;
    if (leftPrimary !== rightPrimary) {
      const primaryScoreDiff = (rightPrimary * 10 + beginnerLocationScore(right)) - (leftPrimary * 10 + beginnerLocationScore(left));
      if (primaryScoreDiff !== 0) return primaryScoreDiff;
    }
    const scoreDiff = beginnerLocationScore(right) - beginnerLocationScore(left);
    if (scoreDiff !== 0) return scoreDiff;
    return left.name.localeCompare(right.name);
  });
};
